import consts, { TypeCanvasElement } from "shared/consts";
import { getElementGraphicsProvider, getElementProvider } from "elements/index";
import type { TransformerConfig } from "elements/base/types";
import { getElementTypeForId, isOfTypes } from "frontend/canvas-designer-new/elements/canvas-elements-utils";

export function isElementConnectable(type: TypeCanvasElement): boolean {
  const provider = getElementProvider(type);
  if (provider && provider.isConnectorsEnabled) {
    return provider.isConnectorsEnabled();
  }
  switch (type) {
    case consts.CANVAS_ELEMENTS.SHAPE:
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK:
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.NOUN_ICON:
    case consts.CANVAS_ELEMENTS.STICKY_NOTE:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.FRAME:
    case consts.CANVAS_ELEMENTS.INTEGRATION:
    case consts.CANVAS_ELEMENTS.TABLE: {
      return true;
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR:
    case consts.CANVAS_ELEMENTS.COMMENT:
    case consts.CANVAS_ELEMENTS.MINDMAP:
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART:
    case consts.CANVAS_ELEMENTS.CARD_STACK:
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE:
    case consts.CANVAS_ELEMENTS.ORG_CHART:
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION:
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return false;
    }
    default: {
      console.warn(`Unknown element type: ${type}`);
      return false;
    }
  }
}

export function isElementLinkBadgeSupporting(type: TypeCanvasElement): boolean | null {
  const provider = getElementProvider(type);
  if (provider) {
    return provider.isLinkBadgeEnabled();
  }
  switch (type) {
    case consts.CANVAS_ELEMENTS.SHAPE:
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK:
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.NOUN_ICON:
    case consts.CANVAS_ELEMENTS.STICKY_NOTE:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.MINDMAP:
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
      return true;
    }
    case consts.CANVAS_ELEMENTS.INTEGRATION:
    case consts.CANVAS_ELEMENTS.CARD_STACK:
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE:
    case consts.CANVAS_ELEMENTS.ORG_CHART:
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION:
    case consts.CANVAS_ELEMENTS.TIMELINE:
    case consts.CANVAS_ELEMENTS.TABLE: {
      return false;
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR:
    case consts.CANVAS_ELEMENTS.FRAME:
    case consts.CANVAS_ELEMENTS.COMMENT: {
      return null;
    }
    default: {
      console.warn(`Unknown element type: ${type}`);
      return false;
    }
  }
}

export function isElementVotable(type: TypeCanvasElement): boolean | null {
  const provider = getElementProvider(type);
  if (provider) {
    return provider.isVotingEnabled();
  }
  switch (type) {
    case consts.CANVAS_ELEMENTS.SHAPE:
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK:
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.STICKY_NOTE:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.MINDMAP:
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART:
    case consts.CANVAS_ELEMENTS.INTEGRATION:
    case consts.CANVAS_ELEMENTS.TABLE: {
      return true;
    }
    case consts.CANVAS_ELEMENTS.CARD_STACK:
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE:
    case consts.CANVAS_ELEMENTS.ORG_CHART:
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION:
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return false;
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR:
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.FRAME:
    case consts.CANVAS_ELEMENTS.COMMENT: {
      return null;
    }
    default: {
      console.warn(`Unknown element type: ${type}`);
      return false;
    }
  }
}

export function textEnabledForElementType(type: TypeCanvasElement) {
  const provider = getElementProvider(type);
  if (provider) {
    return provider.isTextEnabled();
  }
  switch (type) {
    case consts.CANVAS_ELEMENTS.STICKY_NOTE:
    case consts.CANVAS_ELEMENTS.SHAPE: {
      return true;
    }
    case consts.CANVAS_ELEMENTS.FRAME:
    case consts.CANVAS_ELEMENTS.CONNECTOR:
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK:
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.MINDMAP:
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART:
    case consts.CANVAS_ELEMENTS.INTEGRATION:
    case consts.CANVAS_ELEMENTS.TIMELINE:
    case consts.CANVAS_ELEMENTS.TABLE: {
      return false;
    }
    default: {
      return false;
    }
  }
}

export function getSpecialTransformerConfig(type: TypeCanvasElement): Partial<TransformerConfig> {
  const provider = getElementGraphicsProvider(type);
  if (provider) {
    return provider.getTransformerConfig();
  }
  switch (type) {
    case consts.CANVAS_ELEMENTS.FILE: {
      return {
        keepRatio: true,
        lockAspectRatio: true,
      };
    }
    case consts.CANVAS_ELEMENTS.SHAPE:
    case consts.CANVAS_ELEMENTS.CONNECTOR:
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.STICKY_NOTE:
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.FRAME:
    case consts.CANVAS_ELEMENTS.COMMENT:
    case consts.CANVAS_ELEMENTS.MINDMAP:
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART:
    case consts.CANVAS_ELEMENTS.INTEGRATION:
    case consts.CANVAS_ELEMENTS.ORG_CHART:
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE:
    case consts.CANVAS_ELEMENTS.CARD_STACK:
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION:
    case consts.CANVAS_ELEMENTS.TABLE: {
      return {};
    }
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK: {
      return {
        showSnapGuidelines: true,
        snapToGuidelines: false,
      };
    }
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return {
        showSnapGuidelines: false,
        snapToGuidelines: false,
      };
    }
    default: {
      return {};
    }
  }
}

const isTypeDroppableOnTable: Record<TypeCanvasElement, boolean> = {
  shape: true,
  textBlock: true,
  drawing: true,
  stickyNote: true,
  file: true,
  taskCard: true,
  nounIcon: true,
  connector: false,
  frame: false,
  comment: false,
  mindmap: false,
  mindmapOrgChart: false,
  orgChartRoot: false,
  orgChartNode: false,
  integrationItem: false,
  cardStack: false,
  liveIntegration: false,
  timeline: false,
  table: false,
  tableCell: false,
  // Elements that use the new framework - change them in their providers, not here !!
  task: false,
  icon: false,
  gantt: false,
  pdf: false,
  chart: false,
  mondayTask: false,
};

// create a function that will test element-type, or element-id (type-uuid)
const isDroppableOnTable = (() => {
  // get element-types that are droppable from our config object
  const droppables = Object.entries(isTypeDroppableOnTable)
    .filter(([, value]) => value)
    .map(([key]) => key) as TypeCanvasElement[];
  // create a function that tests string ids if they match those 'droppable' types
  return isOfTypes(...droppables);
})();

export function canElementBeDroppedOnTable(typeOrId: TypeCanvasElement | string): boolean {
  const isId = typeOrId.includes("-");
  const type = isId ? getElementTypeForId(typeOrId) : (typeOrId as TypeCanvasElement);
  const provider = getElementProvider(type);
  if (provider) {
    return provider.isTableSupported();
  }
  return isDroppableOnTable(typeOrId);
}
